import React, { useMemo, useState, useRef, useEffect } from "react";
import { COUNTRIES, DEFAULT_COUNTRY, flag } from "@/lib/countries";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";

// Splits an "+233 24 1234567" value into { dial, number }
function splitValue(v) {
  if (!v) return { country: DEFAULT_COUNTRY, number: "" };
  const trimmed = String(v).trim();
  let match = null;
  for (const c of COUNTRIES) {
    if (trimmed.startsWith(c.dial) && (!match || c.dial.length > match.dial.length)) {
      match = c;
    }
  }
  if (match) {
    return { country: match, number: trimmed.slice(match.dial.length).trim() };
  }
  return { country: DEFAULT_COUNTRY, number: trimmed };
}

export default function PhoneInput({ value, onChange, required = true, testid = "phone-input" }) {
  const initial = useMemo(() => splitValue(value), []);  // eslint-disable-line react-hooks/exhaustive-deps
  const [country, setCountry] = useState(initial.country);
  const [number, setNumber] = useState(initial.number);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    onChange(`${country.dial} ${number}`.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, number]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Reset list scroll whenever the search query changes so the first match is always at the top
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [search]);

  // Filter + rank: starts-with matches first, then contains, then dial-code matches
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    const numQ = q.replace(/[^\d+]/g, "");
    const scored = [];
    for (const c of COUNTRIES) {
      const name = c.name.toLowerCase();
      const iso = c.iso.toLowerCase();
      let rank = -1;
      if (name.startsWith(q) || iso === q) rank = 0;
      else if (name.includes(q)) rank = 1;
      else if (numQ && c.dial.includes(numQ)) rank = 2;
      if (rank !== -1) scored.push({ c, rank });
    }
    scored.sort((a, b) => a.rank - b.rank || a.c.name.localeCompare(b.c.name));
    return scored.map((s) => s.c);
  }, [search]);

  const pickCountry = (c) => {
    setCountry(c);
    setOpen(false);
    setSearch("");
  };

  const handleSearchKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[0]) pickCountry(filtered[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <div className="flex">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="input-sharp !w-[120px] flex items-center justify-between gap-1 cursor-pointer pr-2"
          data-testid={`${testid}-country-toggle`}
          aria-label="Select country code"
        >
          <span className="flex items-center gap-2 truncate">
            <span className="text-lg leading-none">{flag(country.iso)}</span>
            <span className="font-mono text-sm">{country.dial}</span>
          </span>
          <CaretDown size={14} className="opacity-60 shrink-0" />
        </button>
        <input
          type="tel"
          required={required}
          inputMode="tel"
          className="input-sharp !flex-1 ml-[-1px]"
          placeholder="Phone number"
          value={number}
          onChange={(e) => setNumber(e.target.value.replace(/[^\d\s-]/g, ""))}
          data-testid={`${testid}-number`}
        />
      </div>

      {open && (
        <div
          className="absolute z-50 mt-1 w-[320px] max-w-[90vw] bg-white border border-black shadow-xl"
          data-testid={`${testid}-menu`}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-black/10 sticky top-0 bg-white">
            <MagnifyingGlass size={14} className="opacity-60" />
            <input
              type="text"
              autoFocus
              placeholder="Type a country — e.g. Ghana"
              className="flex-1 outline-none text-sm bg-transparent text-black placeholder-black/40"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKey}
              data-testid={`${testid}-search`}
            />
            <span className="font-mono text-[10px] text-black/40">{filtered.length}</span>
          </div>
          <ul
            ref={listRef}
            className="phone-country-list overflow-y-scroll overscroll-contain"
            style={{ maxHeight: "min(60vh, 360px)", WebkitOverflowScrolling: "touch" }}
          >
            {filtered.map((c, i) => (
              <li key={c.iso}>
                <button
                  type="button"
                  onClick={() => pickCountry(c)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-black hover:text-white ${
                    c.iso === country.iso ? "bg-neutral-100" : ""
                  } ${i === 0 && search ? "ring-2 ring-inset ring-black/40" : ""}`}
                  data-testid={`${testid}-option-${c.iso}`}
                >
                  <span className="text-lg leading-none">{flag(c.iso)}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="font-mono text-xs opacity-60">{c.dial}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-black/50">No country matches "{search}".</li>
            )}
          </ul>
          {!search && (
            <div className="border-t border-black/10 bg-neutral-50 px-3 py-1.5 text-[10px] font-mono text-black/50 flex items-center justify-between">
              <span>Scroll for all {COUNTRIES.length} countries</span>
              <span>↓</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
